// Utils Manager - 工具管理器
import { itemCategories, defaultCategory, categoryDisplayOrder, validateConfig, getItemResourceInfo, categoryIcons, categoryDescriptions } from '../config/item-categories.js';

class UtilsManager {
  // Generate progress bar
  static generateProgressBar(current, max, length) {
    const percentage = Math.min(current / max, 1);
    const filled = Math.floor(percentage * length);
    const empty = length - filled;
    return '■'.repeat(filled) + '□'.repeat(empty);
  }

  // Get comprehensive item info from resources system
  static getItemInfo(item) {
    const resourceInfo = getItemResourceInfo(item);

    if (!resourceInfo.exists) {
      // Fallback for items not in resources system
      const itemLower = item.toLowerCase();
      if (itemLower === '灵草') {
        return '灵草：服用后可以恢复30点气血。';
      }
      return `${item}：暂无描述。`;
    }

    const { info, category } = resourceInfo;
    const categoryIcon = categoryIcons[category] || '📦';

    // 构建详细信息
    let description = `${categoryIcon} ${item}\n`;
    description += `分类：${category}\n`;
    description += `描述：${info.description}\n`;
    description += `稀有度：${'⭐'.repeat(info.rarity)}\n`;
    description += `价值：${info.value} 灵石\n`;

    // 根据资源类型添加额外信息
    switch (info.type) {
      case 'herb':
        description += '类型：草药\n';
        break;
      case 'mineral':
        description += '类型：矿物\n';
        break;
      case 'beast_part':
        description += '类型：妖兽材料\n';
        break;
      case 'spiritual':
        description += '类型：灵物\n';
        break;
      case 'artifact':
        description += '类型：成品\n';
        break;
    }

    return description.trim();
  }

  // Categorize items based on configuration
  static categorizeItem(item) {
    const itemLower = item.toLowerCase();

    // Sort categories by priority (highest first)
    const sortedCategories = Object.entries(itemCategories)
      .sort(([, a], [, b]) => b.priority - a.priority);

    // Check each category's rules
    for (const [categoryName, categoryConfig] of sortedCategories) {
      for (const rule of categoryConfig.rules) {
        if (this._matchesRule(itemLower, rule)) {
          return categoryName;
        }
      }
    }

    // Return default category if no match found
    return defaultCategory;
  }

  // Check if item matches a specific rule
  static _matchesRule(itemLower, rule) {
    switch (rule.type) {
      case 'exact':
        return rule.keywords.some(keyword =>
          itemLower === keyword.toLowerCase()
        );

      case 'contains':
        return rule.keywords.some(keyword =>
          itemLower.includes(keyword.toLowerCase())
        );

      case 'regex':
        try {
          const regex = new RegExp(rule.pattern, 'i'); // case insensitive
          return regex.test(itemLower);
        } catch (e) {
          console.warn(`Invalid regex pattern: ${rule.pattern}`, e);
          return false;
        }

      default:
        console.warn(`Unknown rule type: ${rule.type}`);
        return false;
    }
  }

  // Get categorized items based on configuration
  static getCategorizedItems(bag) {
    // Initialize categories from config plus default category
    const categories = {};
    Object.keys(itemCategories).forEach(cat => {
      categories[cat] = {};
    });
    categories[defaultCategory] = {};

    for (const [itemName, count] of Object.entries(bag)) {
      const category = this.categorizeItem(itemName);
      categories[category][itemName] = count;
    }

    // Add "全部" category that contains all items
    categories['全部'] = { ...bag };

    return categories;
  }

  // Get category display order
  static getCategoryDisplayOrder() {
    return categoryDisplayOrder;
  }

  // Get all available categories (including default)
  static getAllCategories() {
    return [...categoryDisplayOrder, defaultCategory].filter((cat, index, arr) =>
      arr.indexOf(cat) === index // remove duplicates
    );
  }

  // Validate item categories configuration
  static validateItemCategories() {
    return validateConfig();
  }

  // Get mood description
  static getMoodDescription(mood) {
    const moods = {
      'curious': '充满好奇',
      'excited': '兴奋不已',
      'peaceful': '宁静祥和',
      'frustrated': '略感沮丧',
      'fortunate': '幸运满满',
      'enlightened': '醍醐灌顶',
      'thrilled': '激动万分',
      'relieved': '如释重负',
      'suspicious': '疑虑丛生',
      'elevated': '心境提升',
      'harmonious': '和谐自然',
      'refreshed': '神清气爽',
      'alarmed': '心有余悸',
      'delighted': '喜出望外',
      'awe-struck': '叹为观止',
      'ecstatic': '欣喜若狂',
      'joyful': '满心欢喜',
      'serene': '心平气和',
      'overwhelmed': '震撼不已',
      'contemplative': '深思熟虑',
      'hopeful': '充满希望',
      'focused': '专注投入',
      'adventurous': '冒险精神',
      'dizzy': '头晕目眩',
      'exhilarated': '兴高采烈',
      'blessed': '受宠若惊',
      'wary': '小心翼翼'
    };
    return moods[mood] || mood;
  }

  // Format bag items
  static formatBagItems(bag) {
    const items = Object.keys(bag);
    if (items.length === 0) return ['背包为空。'];
    const itemList = items.map((it, i) => {
      const count = bag[it];
      const countStr = count > 1 ? `×${count}` : '';
      return `${i + 1}. ${it}${countStr} - info ${it}`;
    });
    return ['背包：'].concat(itemList);
  }

  // Validate command input
  static validateCommand(input) {
    if (!input || typeof input !== 'string') {
      return { valid: false, error: '请输入有效的命令。' };
    }
    const trimmed = input.trim();
    if (!trimmed) {
      return { valid: false, error: '请输入命令（直接发送查看帮助）。' };
    }
    return { valid: true, command: trimmed };
  }

  // Parse command arguments
  static parseCommandArgs(input) {
    const trimmed = input.trim();
    return trimmed.split(/\s+/);
  }
}

export default UtilsManager;
